<script setup lang="ts">
import { type VisibilityRule, VisibilityRuleType } from '../../types';
import { Comparison } from 'vince-core/types/common';
import { ButtonType } from 'vince-ui/types/common';

const { t } = useI18n();

const props = defineProps<{
    visibilityRule: VisibilityRule,
    tags: string[],
    formFields: {value: string, label: string}[],
    contextFields: string[],
}>();

const vr = ref<VisibilityRule>(clone(props.visibilityRule));

watch(() => props.visibilityRule, (newValue: VisibilityRule) => {
    vr.value = clone(newValue);
});

const emit = defineEmits(['update:model-value', 'delete']);

const notifyUpdate = () => {
    emit('update:model-value', clone(vr.value));
};

const notifyDelete = () => {
    emit('delete');
};
</script>
<template>
    <div
        class="flex gap-2 mt-8"
    >
        <VLFloatLabel class="w-1/4">
            <VLSelect
                v-model="vr.type"
                fluid
                :options="Object.keys(VisibilityRuleType)"
                :option-label="(v: string) => t(`formBuilder.visibilityRuleType.${v}`)"
                @update:model-value="notifyUpdate"
            />
            <label>{{ t('formBuilder.visibilityRuleTypeLabel') }}</label>
        </VLFloatLabel>

        <VLFloatLabel class="w-1/4">
            <VLSelect
                v-if="vr.type === VisibilityRuleType.CONTEXT"
                v-model="vr.field"
                fluid
                :options="contextFields"
                :option-label="(v: string) => t(`formBuilder.fillContext.${v}`)"
                @update:model-value="notifyUpdate"
            />

            <VLSelect
                v-if="vr.type === VisibilityRuleType.FIELD"
                v-model="vr.field"
                fluid
                :options="formFields"
                option-value="value"
                option-label="label"
                @update:model-value="notifyUpdate"
            />

            <label>{{ t('formBuilder.field') }}</label>
        </VLFloatLabel>

        <VLFloatLabel class="w-1/4">
            <VLSelect
                v-model="vr.comparison"
                fluid
                :options="Object.keys(Comparison)"
                :option-label="(v: string) => t(`comparison.${v}`)"
                @update:model-value="notifyUpdate"
            />
            <label>{{ t('formBuilder.comparison') }}</label>
        </VLFloatLabel>

        <VLFloatLabel class="w-1/4">
            <VLSelect
                v-if="vr.field === 'tags'"
                v-model="vr.value"
                fluid
                :options="tags"
                @update:model-value="notifyUpdate"
            />

            <VLInputText
                v-else
                v-model="vr.value"
                fluid
                @update:model-value="notifyUpdate"
            />

            <label>{{ t('formBuilder.value') }}</label>
        </VLFloatLabel>

        <VButton
            icon="pi pi-trash"
            :type="ButtonType.OUTLINE_PRIMARY"
            @click="notifyDelete"
        />
    </div>
</template>
